import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  type ReactNode,
} from "react";
import { LoginClient } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useAnonNavigationGuard } from "@/hooks/useAnonNavigationGuard";
import { useSupabaseAuth } from "./SupabaseAuthProvider";
import { TermsAcceptanceModal } from "../TermsAcceptanceModal";
import { EphemeralSessionBanner } from "../EphemeralSessionBanner";

export interface EphemeralBannerState {
  visible: boolean;
  onLogin: () => void;
}

interface LoginGateContextValue {
  loginClient: LoginClient;
  setEphemeralBanner: (opts: EphemeralBannerState | null) => void;
}

const LoginGateContext = createContext<LoginGateContextValue | null>(null);

export const useLoginClient = () => {
  const ctx = useContext(LoginGateContext);
  if (!ctx) throw new Error("useLoginClient must be used within LoginGate");
  return ctx.loginClient;
};

export const useSetEphemeralBanner = () => {
  const ctx = useContext(LoginGateContext);
  if (!ctx) throw new Error("useSetEphemeralBanner must be used within LoginGate");
  return ctx.setEphemeralBanner;
};

export const LoginGate = ({ children }: { children: ReactNode }) => {
  const { loading, session, token } = useSupabaseAuth();
  const loginRef = useRef<LoginClient | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ephemeralBanner, setEphemeralBanner] = useState<EphemeralBannerState | null>(null);

  if (token) {
    if (!loginRef.current) {
      loginRef.current = new LoginClient(token);
    } else {
      loginRef.current.setToken(token);
    }
  }

  useEffect(() => {
    if (!session) {
      loginRef.current = null;
    }
  }, [session]);

  const auth = useAuth();
  useAnalytics(auth.isAuthenticated, auth.user?.email);
  useAnonNavigationGuard(auth.isAuthenticated);

  if (loading || !session || !loginRef.current) {
    return (
      <div className="flex h-screen items-center justify-center bg-zinc-950 text-zinc-400" />
    );
  }

  const showTerms =
    !session.user.is_anonymous &&
    !termsAccepted &&
    session.user.user_metadata?.agreed_to_terms !== true;

  return (
    <LoginGateContext.Provider value={{ loginClient: loginRef.current, setEphemeralBanner }}>
      {children}
      <div className="fixed top-3 left-3 z-50 hidden flex-col gap-2 desktop:flex">
        {ephemeralBanner?.visible && (
          <EphemeralSessionBanner
            visible={ephemeralBanner.visible}
            onLogin={ephemeralBanner.onLogin}
          />
        )}
      </div>
      {showTerms && (
        <TermsAcceptanceModal onAccept={() => setTermsAccepted(true)} />
      )}
    </LoginGateContext.Provider>
  );
};
